import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaMapMarkerAlt } from "react-icons/fa";
import { useLocationPreference } from "../contexts/LocationPreferenceContext";
import { isValidGeoCoord } from "../utils/geo";

const LocationPreferencePrompt = () => {
  const {
    showPrompt,
    registeredLocation,
    currentLocation,
    chooseRegisteredLocation,
    chooseCurrentLocation,
    dismissPrompt,
    radiusKm,
    error,
  } = useLocationPreference();
  const [choosing, setChoosing] = useState(false);

  const canChooseRegistered =
    isValidGeoCoord(registeredLocation?.lat, registeredLocation?.lng) || !!registeredLocation?.pincode;
  const canChooseCurrent = isValidGeoCoord(currentLocation?.lat, currentLocation?.lng);

  if (!showPrompt || !registeredLocation || !currentLocation) {
    return null;
  }

  const handleChooseRegistered = async () => {
    if (choosing) return;
    setChoosing(true);
    try {
      await chooseRegisteredLocation();
    } finally {
      setChoosing(false);
    }
  };

  const optionBase =
    "w-full text-left rounded-xl border px-4 py-3.5 transition-colors";
  const optionEnabled =
    "border-slate-600 bg-slate-800/60 hover:border-amber-500/70 hover:bg-amber-500/10 cursor-pointer";
  const optionDisabled =
    "border-slate-700/50 bg-slate-900/40 opacity-50 cursor-not-allowed";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-preference-title"
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          className="w-full max-w-md rounded-2xl border border-slate-600 bg-[#0f172a] shadow-2xl overflow-hidden"
        >
          <div className="flex items-start gap-3 p-5 border-b border-slate-700">
            <div className="w-11 h-11 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
              <FaMapMarkerAlt className="text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 id="location-preference-title" className="text-lg font-bold text-white">
                Choose your location
              </h2>
              <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">
                Your location looks different from your registered address. Which location should we
                use to show shops within {radiusKm} km?
              </p>
            </div>
          </div>

          <div className="p-5 space-y-3">
            <button
              type="button"
              disabled={!canChooseRegistered || choosing}
              onClick={handleChooseRegistered}
              className={`${optionBase} ${
                canChooseRegistered && !choosing ? optionEnabled : optionDisabled
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Registered location
              </p>
              <p className="text-sm font-semibold text-white mt-1.5">
                {registeredLocation.label || "Your saved address"}
              </p>
            </button>

            <button
              type="button"
              disabled={!canChooseCurrent || choosing}
              onClick={chooseCurrentLocation}
              className={`${optionBase} ${
                canChooseCurrent && !choosing ? optionEnabled : optionDisabled
              }`}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Current location
              </p>
              <p className="text-sm font-semibold text-white mt-1.5">
                {currentLocation.label || "Use where you are right now"}
              </p>
              {!canChooseCurrent && (
                <p className="text-xs text-amber-300 mt-1.5">
                  Allow location in browser settings (click the lock icon in the address bar).
                </p>
              )}
            </button>
          </div>

          <div className="px-5 pb-5 space-y-3">
            {error && <p className="text-xs text-rose-400">{error}</p>}
            <p className="text-[11px] text-slate-400 leading-relaxed">
              We ask this every time you open the app so nearby shop results match the location you
              want to use.
            </p>
            <button
              type="button"
              onClick={dismissPrompt}
              className="w-full text-center text-sm font-semibold text-slate-300 hover:text-white transition-colors"
            >
              Browse without nearby filter
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LocationPreferencePrompt;
